/**
 * kikik.id, the front door.
 *
 * The page is a sequence of bands rather than a grid of cards: a jade table
 * with the flagship on it, a mood rail, one argued-for pick, twelve shelves at
 * four sizes, a dark band about learning, and the road ahead. Each band has a
 * different surface, so nothing reads as "another row of the same thing".
 */

import { useEffect, useRef, useState } from "react";
import { getCategory } from "@kikik/game-catalog";
import type { MoodId } from "@kikik/game-catalog";
import { useI18n } from "../i18n";
import { PortalFooter } from "./PortalFooter";
import { PortalHeader } from "./PortalHeader";
import type { NavTarget } from "./PortalHeader";
import { HeroSection } from "./sections/HeroSection";
import { IntentSection } from "./sections/IntentSection";
import { LearnSection } from "./sections/LearnSection";
import { NextSection } from "./sections/NextSection";
import { PicksSection } from "./sections/PicksSection";
import { WorldsSection } from "./sections/WorldsSection";
import { useReveal } from "./useReveal";

const SECTION_FOR: Record<NavTarget, string> = {
  home: "hero",
  games: "games",
  categories: "categories",
  learn: "learn",
};

export interface PortalHomeProps {
  onOpenGame: (slug: string) => void;
  onPlaySolo: () => void;
  onLearn: () => void;
  /** Set by search: scroll here once, after the page has mounted. */
  scrollTo?: NavTarget | null;
}

export function PortalHome({ onOpenGame, onPlaySolo, onLearn, scrollTo }: PortalHomeProps) {
  const { t } = useI18n();
  const [mood, setMood] = useState<MoodId | null>(null);
  const pending = useRef<NavTarget | null>(scrollTo ?? null);
  useReveal(true);

  const goTo = (target: NavTarget): void => {
    const node = globalThis.document?.getElementById(SECTION_FOR[target]);
    if (!node) return;
    // jsdom has no layout engine and no scrollIntoView; the call is optional so
    // the same code runs in tests and in the production smoke check.
    node.scrollIntoView?.({ behavior: "smooth", block: "start" });
    const heading = node.querySelector<HTMLElement>("h1, h2");
    heading?.setAttribute("tabindex", "-1");
    heading?.focus({ preventScroll: true });
  };

  useEffect(() => {
    const target = pending.current;
    if (!target) return;
    pending.current = null;
    goTo(target);
  }, []);

  return (
    <div className="portal" data-screen="home">
      <a className="skiplink" href="#main">
        {t("portal.skipToContent")}
      </a>
      <PortalHeader
        current="home"
        onHome={() => goTo("home")}
        onNavigate={goTo}
        onOpenGame={onOpenGame}
        onOpenCategory={(id) => {
          goTo(getCategory(id) ? "categories" : "home");
        }}
        onOpenMood={(id) => {
          setMood(id as MoodId);
          goTo("games");
        }}
      />

      <main id="main">
        <HeroSection onPlay={onPlaySolo} onLearn={onLearn} />
        <IntentSection active={mood} onSelect={setMood} />
        <PicksSection mood={mood} onOpenGame={onOpenGame} />
        <WorldsSection onOpenGame={onOpenGame} />
        <LearnSection onLearn={onLearn} />
        <NextSection />
      </main>

      <PortalFooter onNavigate={goTo} />
    </div>
  );
}
